import type { SpatialExtent } from '../data/model';
import type { MapView } from './model';

const TILE_SIZE = 512;
const MAX_MERCATOR_LATITUDE = 85.051129;

export interface ExtentViewOptions {
  readonly viewportWidth?: number;
  readonly viewportHeight?: number;
  readonly padding?: number;
  readonly maxZoom?: number;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function mercatorY(latitude: number): number {
  const clampedLatitude = clamp(latitude, -MAX_MERCATOR_LATITUDE, MAX_MERCATOR_LATITUDE);
  const radians = (clampedLatitude * Math.PI) / 180;
  return (1 - Math.asinh(Math.tan(radians)) / Math.PI) / 2;
}

function latitudeFromMercatorY(y: number): number {
  return (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
}

function zoomForSpan(availablePixels: number, worldSpan: number): number {
  if (worldSpan <= Number.EPSILON) return Number.POSITIVE_INFINITY;
  return Math.log2(availablePixels / (TILE_SIZE * worldSpan));
}

function wrapLongitude(longitude: number): number {
  if (longitude >= -180 && longitude <= 180) return longitude;
  return ((((longitude + 180) % 360) + 360) % 360) - 180;
}

export function viewForExtent(extent: SpatialExtent, options: ExtentViewOptions = {}): MapView {
  const viewportWidth = options.viewportWidth ?? 960;
  const viewportHeight = options.viewportHeight ?? 640;
  const padding = options.padding ?? 72;
  const maxZoom = options.maxZoom ?? 13;
  const availableWidth = Math.max(1, viewportWidth - padding * 2);
  const availableHeight = Math.max(1, viewportHeight - padding * 2);
  const longitudeSpan = Math.max(0, extent.east - extent.west) / 360;
  const northY = mercatorY(extent.north);
  const southY = mercatorY(extent.south);
  const latitudeSpan = Math.abs(southY - northY);
  const zoom = clamp(
    Math.min(
      zoomForSpan(availableWidth, longitudeSpan),
      zoomForSpan(availableHeight, latitudeSpan),
    ),
    0,
    maxZoom,
  );
  const centerY = (northY + southY) / 2;

  return {
    center: [wrapLongitude((extent.west + extent.east) / 2), latitudeFromMercatorY(centerY)],
    zoom,
    bearing: 0,
    pitch: 0,
  };
}
